import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../context/AuthProvider'
import { useUserRole } from '../../../context/UserRoleProvider'
import { apiRequest } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import {
  ArrowLeft,
  Loader2,
  Trash2,
  Megaphone,
  Calendar,
  Users,
} from 'lucide-react'
import EventForm from '../../../components/events/EventForm'
import OccurrenceTable from '../../../components/events/OccurrenceTable'
import EventAnnouncementForm from '../../../components/events/EventAnnouncementForm'
import AttendeeList from '../../../components/events/AttendeeList'

export default function ParishEventDetail() {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { getParishAdminParishId } = useUserRole()
  const [parishId, setParishId] = useState(null)
  const [event, setEvent] = useState(null)
  const [occurrences, setOccurrences] = useState([])
  const [announcements, setAnnouncements] = useState([])
  const [rsvpData, setRsvpData] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [postingAnnouncement, setPostingAnnouncement] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const resolveParishId = async () => {
    let id = getParishAdminParishId()
    if (!id) {
      const userData = await apiRequest('/api/v1/auth/me')
      const roleDetails = userData?.roleDetails || []
      const parishAdminRole = roleDetails.find((r) => r.name === 'parish-admin')
      id = parishAdminRole?.parishId
    }
    return id
  }

  const fetchEvent = async (pId) => {
    const data = await apiRequest(`/api/v1/parishes/${pId}/events/${eventId}`)
    setEvent(data)
  }

  const fetchOccurrences = async (pId) => {
    const data = await apiRequest(
      `/api/v1/parishes/${pId}/events/${eventId}/occurrences?limit=50`,
    )
    setOccurrences(data.data || data || [])
  }

  const fetchAnnouncements = async (pId) => {
    const data = await apiRequest(
      `/api/v1/parishes/${pId}/events/${eventId}/announcements`,
    )
    setAnnouncements(data.data || data || [])
  }

  const fetchRsvps = async (pId, occurrenceId) => {
    try {
      const data = await apiRequest(
        `/api/v1/parishes/${pId}/events/${eventId}/occurrences/${occurrenceId}/rsvps`,
      )
      setRsvpData((prev) => ({ ...prev, [occurrenceId]: data }))
    } catch {
      // ignore
    }
  }

  const loadAll = async () => {
    try {
      setLoading(true)
      setError(null)
      const pId = await resolveParishId()
      if (!pId) {
        setError('Parish not found.')
        setLoading(false)
        return
      }
      setParishId(pId)
      await Promise.all([
        fetchEvent(pId),
        fetchOccurrences(pId),
        fetchAnnouncements(pId),
      ])
    } catch (err) {
      console.error('Failed to load event:', err)
      setError(err.message || 'Failed to load event')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAll()
  }, [eventId, user?.id])

  // Fetch RSVPs when occurrences load
  useEffect(() => {
    if (parishId && occurrences.length > 0) {
      occurrences.forEach((occ) => fetchRsvps(parishId, occ.id))
    }
  }, [occurrences, parishId])

  const handleUpdateEvent = async (data) => {
    if (!parishId) return
    try {
      setSaving(true)
      await apiRequest(`/api/v1/parishes/${parishId}/events/${eventId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
      await fetchEvent(parishId)
      alert('Event updated successfully.')
    } catch (err) {
      console.error('Failed to update event:', err)
      alert(err.message || 'Failed to update event')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteEvent = async () => {
    if (!parishId) return
    if (!window.confirm('Are you sure you want to delete this event? This cannot be undone.'))
      return
    try {
      setDeleting(true)
      await apiRequest(`/api/v1/parishes/${parishId}/events/${eventId}`, {
        method: 'DELETE',
      })
      navigate('/app/parish/events')
    } catch (err) {
      console.error('Failed to delete event:', err)
      alert(err.message || 'Failed to delete event')
    } finally {
      setDeleting(false)
    }
  }

  const handleUpdateOccurrence = async (occurrenceId, data) => {
    if (!parishId) return
    try {
      await apiRequest(
        `/api/v1/parishes/${parishId}/events/${eventId}/occurrences/${occurrenceId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        },
      )
      await fetchOccurrences(parishId)
    } catch (err) {
      console.error('Failed to update occurrence:', err)
      alert(err.message || 'Failed to update occurrence')
    }
  }

  const handlePostAnnouncement = async (data) => {
    if (!parishId) return
    try {
      setPostingAnnouncement(true)
      await apiRequest(
        `/api/v1/parishes/${parishId}/events/${eventId}/announcements`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        },
      )
      await fetchAnnouncements(parishId)
    } catch (err) {
      console.error('Failed to post announcement:', err)
      alert(err.message || 'Failed to post announcement')
    } finally {
      setPostingAnnouncement(false)
    }
  }

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!parishId) return
    try {
      await apiRequest(
        `/api/v1/parishes/${parishId}/events/${eventId}/announcements/${announcementId}`,
        { method: 'DELETE' },
      )
      await fetchAnnouncements(parishId)
    } catch (err) {
      console.error('Failed to delete announcement:', err)
      alert(err.message || 'Failed to delete announcement')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded p-6">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    )
  }

  if (!event) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white border border-surface-border rounded p-6">
          <p className="text-charcoal-muted">Event not found.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/app/parish/events')}
            className="text-charcoal hover:text-charcoal-muted mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Events
          </Button>
          <h1 className="text-3xl font-bold font-display text-charcoal">
            {event.title}
          </h1>
        </div>
        <Button
          variant="outline"
          onClick={handleDeleteEvent}
          disabled={deleting}
          className="text-red-600 border-red-200 hover:bg-red-50"
        >
          {deleting ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Trash2 className="w-4 h-4 mr-2" />
          )}
          Delete Event
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Edit Form */}
          <div className="bg-white border border-surface-border rounded p-6">
            <h2 className="text-xl font-semibold font-display text-charcoal mb-4">
              Edit Event
            </h2>
            <EventForm
              initialValues={event}
              onSubmit={handleUpdateEvent}
              loading={saving}
              parishId={parishId}
            />
          </div>

          {/* Occurrence Management */}
          <div className="bg-white border border-surface-border rounded p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-gold" />
              <h2 className="text-xl font-semibold font-display text-charcoal">
                Occurrences
              </h2>
            </div>
            <OccurrenceTable
              occurrences={occurrences}
              onUpdate={handleUpdateOccurrence}
            />
          </div>

          {/* Announcements */}
          <div className="bg-white border border-surface-border rounded p-6">
            <div className="flex items-center gap-2 mb-4">
              <Megaphone className="w-5 h-5 text-gold" />
              <h2 className="text-xl font-semibold font-display text-charcoal">
                Announcements
              </h2>
            </div>

            <EventAnnouncementForm
              onSubmit={handlePostAnnouncement}
              loading={postingAnnouncement}
            />

            {announcements.length > 0 && (
              <div className="mt-6 pt-6 border-t border-surface-border space-y-4">
                {announcements.map((ann) => (
                  <div
                    key={ann.id}
                    className="flex items-start justify-between gap-4 py-3 px-4 rounded bg-surface-linen/50 border border-surface-border"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-charcoal">
                          {ann.title}
                        </h4>
                        {ann.isPublished ? (
                          <span className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                            Published
                          </span>
                        ) : (
                          <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                            Draft
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-charcoal/80 whitespace-pre-wrap">
                        {ann.content}
                      </p>
                      <p className="text-xs text-charcoal-muted mt-1">
                        {ann.author?.name || 'Staff'} &middot;{' '}
                        {new Date(ann.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 flex-shrink-0"
                      onClick={() => handleDeleteAnnouncement(ann.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - RSVPs */}
        <div className="space-y-6">
          {occurrences.map((occ) => {
            const rd = rsvpData[occ.id]
            if (!rd || rd.count === 0) return null
            return (
              <div
                key={occ.id}
                className="bg-white border border-surface-border rounded p-6"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-gold" />
                  <h3 className="text-lg font-semibold font-display text-charcoal">
                    RSVPs
                  </h3>
                </div>
                <p className="text-xs text-charcoal-muted mb-3">
                  {new Date(occ.startTime).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                  {' - '}
                  {rd.count} {rd.count === 1 ? 'person' : 'people'}
                </p>
                <AttendeeList rsvps={rd.rsvps || []} />
              </div>
            )
          })}

          {occurrences.length > 0 &&
            occurrences.every((occ) => {
              const rd = rsvpData[occ.id]
              return !rd || rd.count === 0
            }) && (
              <div className="bg-white border border-surface-border rounded p-6 text-center">
                <Users className="w-10 h-10 text-charcoal-faint mx-auto mb-2" />
                <p className="text-charcoal-muted text-sm">No RSVPs yet.</p>
              </div>
            )}
        </div>
      </div>
    </div>
  )
}
